import { Map, MapEvent } from '@vis.gl/react-google-maps';
import { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { LoadingView } from '~/components';
import { useAppDispatch, useAppSelector } from '~/state';
import { selectLocation, setLocationCoordinates } from '~/state/location';
import { setMapBounds, setMapInteractionActive } from '~/state/mapData';
import {
  GOOGLE_MAP_ID,
  LoadingStatus,
  SearchResultGroupByLocation,
  Views,
  getBounds,
} from '~/utilities';
import { captureException } from '@sentry/react';
import {
  clearAllSelected,
  selectSelectedClinic,
  selectSelectedPractitioner,
} from '~/state/activeSelection';
import { selectDiscipline } from '~/state/discipline';
import { MarkersList } from './MarkersList';
import cn from 'classnames';
import { useDebounceCallback } from 'usehooks-ts';

const DEFAULT_ZOOM = 13;

interface MapContainerProps {
  view: Views;
  results: SearchResultGroupByLocation[];
  loading?: LoadingStatus;
}

export const MapContainer = memo(
  ({ view, results, loading = 'idle' }: MapContainerProps) => {
    const dispatch = useAppDispatch();
    const [mapLoading, setMapLoading] = useState(true);
    const globalLocation = useAppSelector(selectLocation);
    const selectedClinic = useAppSelector(selectSelectedClinic);
    const selectedPractitioner = useAppSelector(selectSelectedPractitioner);
    const discipline = useAppSelector(selectDiscipline);

    const [location, setLocalLocation] = useState(globalLocation);

    // Calculate restriction dynamically after feature flags are initialized
    const mapRestriction = useMemo(
      (): google.maps.MapRestriction => ({
        latLngBounds: getBounds(),
        strictBounds: false,
      }),
      []
    );

    useEffect(() => {
      if (!globalLocation) return;
      setLocalLocation(globalLocation);
    }, [globalLocation]);

    const refetchResults = useCallback(
      (event: MapEvent) => {
        const center = event.map.getCenter();
        const bounds = event.map.getBounds();

        if (!center || !bounds) {
          captureException(
            new Error(
              `Error on getting map coordinates on ${event.type} callback in MapContainer.tsx. {center: ${center}, bounds: ${bounds}}`
            )
          );
          return;
        }

        const centerLiteral: google.maps.LatLngLiteral = {
          lat: center.lat(),
          lng: center.lng(),
        };

        const boundsLiteral: google.maps.LatLngBoundsLiteral = {
          east: bounds.getNorthEast().lng(),
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          west: bounds.getSouthWest().lng(),
        };

        dispatch(setMapInteractionActive(true));
        dispatch(setMapBounds(boundsLiteral));
        dispatch(setLocationCoordinates({ ...centerLiteral }));
      },
      [dispatch]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedRefetch = useCallback(
      useDebounceCallback(refetchResults, 500, {
        leading: false,
        trailing: true,
      }),
      []
    );

    const handleViewportChange = (event: MapEvent) => {
      // Dont refetch when discipline or location are missing, or map is hidden (in list view)
      if (!discipline || !location || view === Views.List) return;

      if (selectedClinic || selectedPractitioner) {
        dispatch(clearAllSelected());
      }

      if (event.type === 'zoom_changed') {
        debouncedRefetch(event);
        return;
      }

      refetchResults(event);
    };

    return (
      <div
        data-testid="map-container"
        className={cn(
          'grid-area-map focus-within:outline-none focus:outline-none',
          view !== Views.List ? 'grid' : 'hidden'
        )}
      >
        <Map
          disableDefaultUI
          zoomControl
          defaultCenter={location?.coordinates}
          defaultZoom={DEFAULT_ZOOM}
          minZoom={DEFAULT_ZOOM - 2}
          maxZoom={DEFAULT_ZOOM + 4}
          mapId={GOOGLE_MAP_ID}
          backgroundColor="#ffffff"
          onTilesLoaded={() => setMapLoading(false)}
          restriction={mapRestriction}
          onClick={() => {
            (selectedClinic || selectedPractitioner) &&
              dispatch(clearAllSelected());
          }}
          onDragend={handleViewportChange}
          onZoomChanged={handleViewportChange}
        >
          {(mapLoading || loading === 'pending') && (
            <div className="flex h-full items-center justify-center">
              <div className="absolute inset-0 flex items-center justify-center">
                <LoadingView size="4xl" loading={true} noMessage />
              </div>
            </div>
          )}
          <MarkersList
            loading={loading}
            mapRestriction={mapRestriction}
            results={results}
          />
        </Map>
      </div>
    );
  }
);

MapContainer.displayName = 'MapContainer';
